import React, { useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_PREFIX = 'cm.colWidths.';
const MIN_COL_WIDTH = 60;

/* ──────────────────────────────────────────────────────────────────────
   useResizableColumns
   Persists per-column widths in localStorage under `cm.colWidths.<key>`.
   Pass a stable tableKey + a defaults object. Returns { widths, setWidth }.
   The bound resize handle binds to a column key — see <ResizeHandle/>.
   ────────────────────────────────────────────────────────────────────── */
export function useResizableColumns(tableKey, defaults) {
  const storageKey = `${STORAGE_PREFIX}${tableKey}`;
  const [widths, setWidths] = useState(() => {
    if (typeof window === 'undefined') return defaults;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw);
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      // ignore quota / disabled storage
    }
  }, [storageKey, widths]);

  const setWidth = useCallback(
    (key) => (next) => setWidths((prev) => ({ ...prev, [key]: next })),
    []
  );

  return { widths, setWidth };
}

/* ──────────────────────────────────────────────────────────────────────
   ResizeHandle — drag strip on the right edge of a header cell.
   Renders a visible 1px gray divider (so the user can see where to
   grab) that thickens to a 2px teal line on hover or while dragging.
   The hit zone is 8px wide centered on the divider for forgiving aim.
   Parent header cell must be position: relative.
   ────────────────────────────────────────────────────────────────────── */
export function ResizeHandle({ onResize, getCurrentWidth }) {
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(0);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const dx = e.clientX - startXRef.current;
      const next = Math.max(MIN_COL_WIDTH, startWidthRef.current + dx);
      onResize(next);
    };
    const onUp = () => setDragging(false);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, onResize]);

  const onDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startWidthRef.current = getCurrentWidth();
    setDragging(true);
  };

  const active = hover || dragging;
  return (
    <div
      onPointerDown={onDown}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onClick={(e) => e.stopPropagation()}
      title="Drag to resize column"
      style={{
        position: 'absolute',
        top: 0,
        right: -4,
        height: '100%',
        width: 8,
        cursor: 'col-resize',
        userSelect: 'none',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: active ? 2 : 1,
          height: '100%',
          background: active ? '#115e59' : '#d1d5db',
          transition: 'background 120ms ease, width 120ms ease',
        }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   ColumnDivider — a static 1px gray vertical line matching the resting
   state of <ResizeHandle>. Use this on the right edge of non-resizable
   header cells (typically flex-1 / slack-absorbing columns) so the
   column boundary stays visible and consistent across the table.
   Parent must be position: relative.
   ────────────────────────────────────────────────────────────────────── */
export function ColumnDivider() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        width: 1,
        background: '#d1d5db',
        pointerEvents: 'none',
      }}
    />
  );
}

/* ──────────────────────────────────────────────────────────────────────
   useSortableData — client-side sort over a row array.
   Three-state cycle: asc → desc → cleared (returns rows in original order).
   `accessors` maps a column key to a function (row) => sortable value.
   If a column key has no accessor, falls back to row[key].
   ────────────────────────────────────────────────────────────────────── */
export function useSortableData(rows, accessors = {}) {
  const [sort, setSort] = useState({ key: null, dir: null });

  const cycle = useCallback((key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!sort.key || !sort.dir) return rows;
    const accessor = accessors[sort.key] || ((r) => r?.[sort.key]);
    const dirMul = sort.dir === 'desc' ? -1 : 1;
    // Compare nulls FIRST and short-circuit before applying the direction
    // multiplier, so nulls always sink to the bottom regardless of asc/desc.
    // (Previously the function sorted asc then reversed for desc — which
    // flipped null-handling and yanked nulls to the top on desc.)
    const out = [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      const aNull = av === null || av === undefined || av === '';
      const bNull = bv === null || bv === undefined || bv === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else if (av instanceof Date && bv instanceof Date) {
        cmp = av.getTime() - bv.getTime();
      } else {
        // Numeric-aware string compare so embedded numbers sort by value
        // ("9" < "10", "MED-2" < "MED-10") instead of byte-by-byte. Callers
        // should still pre-parse currency / formatted strings to a Number
        // in their accessor (see Billing.amountRaw, FinanceTable.amount).
        cmp = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }
      return cmp * dirMul;
    });
    return out;
  }, [rows, sort, accessors]);

  return { sortedRows, sort, requestSort: cycle };
}

/* ──────────────────────────────────────────────────────────────────────
   SortableHeader — wraps header label, reveals sort indicator on hover
   (or always when actively sorted on this column).
   Click anywhere on the header to cycle asc → desc → none.
   ────────────────────────────────────────────────────────────────────── */
export function SortableHeader({ label, sortKey, sort, onSort, className = '', style, children }) {
  const active = sort?.key === sortKey && sort?.dir;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`group flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left ${className}`}
      style={style}
    >
      <span>{children ?? label}</span>
      <SortIndicator dir={active ? sort.dir : null} />
    </button>
  );
}

function SortIndicator({ dir }) {
  // Always render the slot so column widths don't jiggle on sort.
  // Inactive state is invisible and fades in to full opacity on hover;
  // active state stays fully visible. Icon is intentionally chunky so
  // the affordance reads at a glance.
  const opacityClass = dir ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';
  const ascColor = dir === 'asc' ? '#115e59' : '#0e1e20';
  const descColor = dir === 'desc' ? '#115e59' : '#0e1e20';
  const ascOpacity = dir === 'asc' ? 1 : dir === 'desc' ? 0.35 : 0.85;
  const descOpacity = dir === 'desc' ? 1 : dir === 'asc' ? 0.35 : 0.85;
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center transition-opacity duration-150 ${opacityClass}`}
      style={{ width: 16, height: 16 }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M3.5 6L7 2.5L10.5 6"
          stroke={ascColor}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={ascOpacity}
        />
        <path
          d="M3.5 8L7 11.5L10.5 8"
          stroke={descColor}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={descOpacity}
        />
      </svg>
    </span>
  );
}
